import { normalizeWorkMode } from 'utils/workMode';

const toBoolean = (value: any) => Boolean(value === true || value === 'true' || value === 1 || value === '1');

const buildBasicExpectationFromHints = (expectations: any, message: any) => {
  if (!expectations && !message) return null;

  // The talent audit criteria payload has no basic expectation at all — no
  // employment type, salary, experience level. Without this guard we'd hand the
  // criteria view an object of empty strings and it would render a blank section.
  const hasAnyBasicExpectationField = [
    'employment_type',
    'work_mode',
    'address',
    'experience_level',
    'years_of_experience',
    'experience_environment',
    'min_salary_range_amount',
    'max_salary_range_amount',
    'salary_range_currency',
    'salary_range_term',
  ].some((field) => !!message?.[field] || !!expectations?.[field]);
  const hasSalaryRange = !!expectations?.salary_range?.min || !!expectations?.salary_range?.max;
  if (!hasAnyBasicExpectationField && !hasSalaryRange) return null;

  const expectationSource = expectations || {};
  const messageSource = message || {};
  const salaryRange = expectationSource.salary_range || {};
  const coreResponsibilities = expectationSource.core_responsibilities || messageSource.core_responsibilities;

  return {
    employmentType: messageSource.employment_type || expectationSource.employment_type?.recommended || '',
    workMode: normalizeWorkMode(messageSource.work_mode || expectationSource.work_mode?.recommended || ''),
    jobLocation: messageSource.address
      || expectationSource.address?.recommended
      || expectationSource.address
      || '',
    experienceLevel: messageSource.experience_level || expectationSource.experience_level?.recommended || '',
    yearsOfExperience: messageSource.years_of_experience || expectationSource.years_of_experience?.recommended || '',
    workEnvironment: messageSource.experience_environment || expectationSource.experience_environment?.recommended || '',
    minSalary: messageSource.min_salary_range_amount?.toString?.() || salaryRange.min?.toString?.() || '',
    maxSalary: messageSource.max_salary_range_amount?.toString?.() || salaryRange.max?.toString?.() || '',
    salaryCurrency: messageSource.salary_range_currency || expectationSource.salary_range_currency?.recommended || '',
    salaryPeriod: messageSource.salary_range_term || expectationSource.salary_range_term?.recommended || '',
    isEmploymentType: toBoolean(messageSource.require_employment_type ?? expectationSource.employment_type?.required ?? true),
    isWorkMode: toBoolean(messageSource.require_work_mode ?? expectationSource.work_mode?.required ?? true),
    isExperienceLevel: toBoolean(messageSource.require_experience_level ?? expectationSource.experience_level?.required ?? true),
    isYearsOfExperience: toBoolean(messageSource.require_years_of_experience ?? expectationSource.years_of_experience?.required ?? true),
    isWorkEnvironment: toBoolean(messageSource.require_work_environment ?? expectationSource.experience_environment?.required ?? true),
    isSalaryRange: toBoolean(messageSource.require_salary_range ?? (!!salaryRange.min || !!salaryRange.max)),
    displaySalary: toBoolean(messageSource.display_salary ?? expectationSource.display_salary),
    isCV: toBoolean(messageSource.require_cv_upload ?? expectationSource.require_cv_upload ?? true),
    isPortfolio: toBoolean(messageSource.require_portfolio_upload ?? expectationSource.require_portfolio_upload ?? true),
    coreResponsibilities,
  };
};

export const parseCriteriaData = (responseMessage: any) => {
  const message = responseMessage?.message;
  const hints = responseMessage?.ui_hints?.expectations || message?.ui_hints?.expectations;

  if (!message && !hints) return null;

  const basicExpectation =
    message?.basic_expectation ||
    buildBasicExpectationFromHints(hints, message);

  const coreResponsibilities =
    message?.core_responsibilities ||
    hints?.core_responsibilities ||
    basicExpectation?.coreResponsibilities ||
    null;

  // The talent audit names this dimension "operational competence", but the shape
  // is identical to technical skills so it maps onto the same view model. Which
  // key it arrived under is the per-payload signal for the section heading —
  // deriving that from the data rather than the current flow keeps the heading
  // correct even when an audit and a hiring conversation are viewed back to back.
  const namedTechnicalSkills = message?.technical_skills || hints?.technical_skills || null;
  const operationalCompetence = message?.operational_competence || hints?.operational_competence || null;
  const technicalSkills = namedTechnicalSkills || operationalCompetence;
  const softSkills = message?.soft_skills || hints?.soft_skills || null;
  const workCulture = message?.work_culture || hints?.work_culture || null;
  const personality = message?.personality || hints?.personality || null;
  const workStyles = message?.work_styles || hints?.work_styles || null;

  const workMotivators = workCulture?.work_motivators || workCulture?.motivators || workCulture?.culture || workCulture || null;
  const workplaceQualities = workCulture?.workplace_qualities || workCulture?.qualities || workCulture?.quality_options || [];

  return {
    basic_expectation: basicExpectation,
    core_responsibilities: coreResponsibilities,
    technical_skills: technicalSkills,
    uses_operational_competence: !namedTechnicalSkills && !!operationalCompetence,
    soft_skills: softSkills,
    work_culture: workCulture,
    work_motivators: workMotivators,
    workplace_qualities: workplaceQualities,
    personality,
    work_styles: workStyles,
    candidate_qualities: workCulture?.quality_options || hints?.work_culture?.quality_options || [],
  };
};
